import { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MultiSelectDropdown from './MultiSelectDropdown';
import { useDialogA11y } from '../../components/dialog/useDialogA11y';
import { LpiAdvancedSelection, EMPTY_LPI_ADVANCED } from './types';
import './LpiAdvancedFilterDrawer.scss';

let drawerInstanceId = 0;

interface LpiAdvancedFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: LpiAdvancedSelection) => void;
  onReset: () => void;
  filters: LpiAdvancedSelection;
  vehicles: string[];
  modalityDetails: string[];
  carriers: string[];
  isLoadingVehicles?: boolean;
  isLoadingModalityDetails?: boolean;
  isLoadingCarriers?: boolean;
  onVehiclesOpen?: () => void;
  onModalityDetailsOpen?: () => void;
  onCarriersOpen?: () => void;
}

/**
 * Advanced Filters drawer for the LPI page, following the CPI drawer so the two
 * report pages behave alike.
 *
 * Unlike the CPI drawer this one does not fetch its own options: LpiReports
 * already owns the vehicle / modality / carrier lists and lazy-loads them on
 * first open, so the options and their loading flags arrive as props.
 *
 * Selections are held locally and only reported on Apply, so opening the
 * drawer, poking at dropdowns and closing it leaves the results untouched.
 */
const LpiAdvancedFilterDrawer: FC<LpiAdvancedFilterDrawerProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  filters,
  vehicles,
  modalityDetails,
  carriers,
  isLoadingVehicles = false,
  isLoadingModalityDetails = false,
  isLoadingCarriers = false,
  onVehiclesOpen,
  onModalityDetailsOpen,
  onCarriersOpen,
}) => {
  const { t } = useTranslation();

  const dialogRef = useRef<HTMLDivElement>(null);
  const [titleId] = useState(() => {
    drawerInstanceId += 1;
    return `lpi-filter-drawer-title-${drawerInstanceId}`;
  });

  useDialogA11y({ isOpen, onClose, containerRef: dialogRef });

  const [localFilters, setLocalFilters] = useState<LpiAdvancedSelection>(filters);
  // Re-sync when the parent changes them behind our back — removing a pill, say.
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Only one dropdown menu open at a time; they overlap otherwise in a panel
  // this narrow.
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(EMPTY_LPI_ADVANCED);
    onReset();
    onClose();
  };

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      {isOpen && <div className="lpi-advanced-filter-drawer__overlay" onClick={onClose} />}

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`lpi-advanced-filter-drawer ${isOpen ? 'lpi-advanced-filter-drawer--open' : ''}`}
      >
        <div className="lpi-advanced-filter-drawer__header">
          <h3 id={titleId}>{t('lpi.advancedFilters')}</h3>
          <button type="button" className="lpi-advanced-filter-drawer__close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="lpi-advanced-filter-drawer__body">
          <MultiSelectDropdown
            label={t('lpi.advancedFilters.vehicle')}
            options={vehicles}
            selected={localFilters.vehicle_id}
            onChange={(val: string[]) => setLocalFilters((prev) => ({ ...prev, vehicle_id: val }))}
            placeholder={t('All Vehicles')}
            isLoading={isLoadingVehicles}
            onOpen={() => { setOpenDropdown('vehicle'); onVehiclesOpen?.(); }}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'vehicle'}
            showSelectAll={false}
            minWidth="100%"
          />

          <MultiSelectDropdown
            label={t('lpi.advancedFilters.modalityDetail')}
            options={modalityDetails}
            selected={localFilters.modality_detail}
            onChange={(val: string[]) => setLocalFilters((prev) => ({ ...prev, modality_detail: val }))}
            placeholder={t('All Modality Details')}
            isLoading={isLoadingModalityDetails}
            onOpen={() => { setOpenDropdown('modalityDetail'); onModalityDetailsOpen?.(); }}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'modalityDetail'}
            showSelectAll={false}
            minWidth="100%"
          />

          <MultiSelectDropdown
            label={t('lpi.advancedFilters.carrier')}
            options={carriers}
            selected={localFilters.carrier_id}
            onChange={(val: string[]) => setLocalFilters((prev) => ({ ...prev, carrier_id: val }))}
            placeholder={t('All Carriers')}
            isLoading={isLoadingCarriers}
            onOpen={() => { setOpenDropdown('carrier'); onCarriersOpen?.(); }}
            onClose={() => setOpenDropdown(null)}
            menuIsOpen={openDropdown === 'carrier'}
            showSelectAll={false}
            minWidth="100%"
          />
        </div>

        <div className="lpi-advanced-filter-drawer__footer">
          <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
            {t('lpi.advancedFilters.reset')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply}>
            {t('lpi.advancedFilters.apply')}
          </button>
        </div>
      </div>
    </>
  );
};

export default LpiAdvancedFilterDrawer;
